import React, { useEffect, useMemo, useState } from 'react';
import MultiSelectTags from '../MultiSelectTags';
import { tiposOrganizacion } from '../../data/profileData';
import {
  fetchBusinessRoundInterestedParticipants,
  type AddBusinessRoundParticipantResult,
  type BusinessRoundInterestedParticipant,
} from './networkingApi';
import { isParticipantAlreadyInManualContacts } from './participantManualMatch';
import type { ManualContactRow } from './PartnerManualFields';

interface BusinessRoundsInterestedModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Si false, no se puede agregar otra contraparte (tope de 5). */
  canAddParticipant?: boolean;
  /** Contrapartes ya cargadas en el formulario (oculta el botón en esas tarjetas). */
  addedContacts?: ManualContactRow[];
  onSelectParticipant?: (
    participant: BusinessRoundInterestedParticipant
  ) => AddBusinessRoundParticipantResult | void;
  contentClassName?: string;
}

const FILTER_DEBOUNCE_MS = 300;

const ADD_FEEDBACK_MS = 4000;

function ParticipantCatalogCard({
  participant: p,
  selectable,
  onSelect,
  justAdded,
}: {
  participant: BusinessRoundInterestedParticipant;
  selectable: boolean;
  onSelect?: () => void;
  justAdded?: boolean;
}) {
  const typeLabel = (p.organizationType || '').trim();
  const role = (p.position || '').trim();
  const showPhone =
    !p.hideContact && (p.sources || []).includes('catalog_initiative');
  const phone = showPhone ? (p.phone || '').trim() : '';
  const email = p.hideContact ? '' : (p.email || '').trim();
  const hasPublishedContact = !p.hideContact && !!(email || phone);

  return (
    <article
      className="net-initiative-card net-initiative-card--readonly net-initiative-card--catalog net-initiative-card--brr-participant"
      role="listitem"
    >
      <div className="net-initiative-card-stack">
        <div className="net-initiative-card-head">
          {typeLabel ? (
            <div className="net-initiative-card-badge-row">
              <span className="net-initiative-card-badge">{typeLabel}</span>
            </div>
          ) : null}
          <h3 className="net-initiative-card-title">{p.fullName}</h3>
        </div>
        {p.organization ? <span className="net-initiative-card-org">{p.organization}</span> : null}
        <div
          className="net-initiative-card-contact net-initiative-card-contact--catalog-stack"
          aria-label="Datos de contacto del participante"
        >
          <h4 className="net-catalog-contact-heading">Datos de contacto</h4>
          {role ? (
            <div className="net-catalog-contact-item">
              <span className="net-catalog-contact-label">Cargo</span>
              <span className="net-catalog-contact-value">{role}</span>
            </div>
          ) : null}
          {!p.hideContact && email ? (
            <div className="net-catalog-contact-item">
              <span className="net-catalog-contact-label">Correo</span>
              <span className="net-catalog-contact-value">{email}</span>
            </div>
          ) : null}
          {!p.hideContact && phone ? (
            <div className="net-catalog-contact-item">
              <span className="net-catalog-contact-label">Teléfono</span>
              <span className="net-catalog-contact-value">{phone}</span>
            </div>
          ) : null}
          {!p.hideContact && !hasPublishedContact && !role ? (
            <p className="net-catalog-contact-empty">Sin datos de contacto</p>
          ) : null}
        </div>
        {selectable && onSelect ? (
          <div className="net-brr-card-actions">
            <button
              type="button"
              className={`save-profile-button net-brr-use-btn${justAdded ? ' net-brr-use-btn--added' : ''}`}
              onClick={onSelect}
              disabled={justAdded}
              aria-live="polite"
            >
              {justAdded ? '✓ Agregado' : 'Agregar como contraparte'}
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

const BusinessRoundsInterestedModal: React.FC<BusinessRoundsInterestedModalProps> = ({
  isOpen,
  onClose,
  canAddParticipant = true,
  addedContacts = [],
  onSelectParticipant,
  contentClassName = '',
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<BusinessRoundInterestedParticipant[]>([]);
  const [catalogQ, setCatalogQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [filterOrganizationTypes, setFilterOrganizationTypes] = useState<string[]>([]);
  const [addNotice, setAddNotice] = useState<{ fullName: string; counterpartyNumber: number } | null>(
    null
  );
  const [recentlyAddedId, setRecentlyAddedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCatalogQ('');
      setDebouncedQ('');
      setFilterOrganizationTypes([]);
      setAddNotice(null);
      setRecentlyAddedId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBusinessRoundInterestedParticipants()
      .then((r) => {
        if (!cancelled) setParticipants(r.data || []);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error al cargar participantes');
          setParticipants([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    const delay = catalogQ.trim() ? FILTER_DEBOUNCE_MS : 0;
    const t = window.setTimeout(() => setDebouncedQ(catalogQ.trim()), delay);
    return () => window.clearTimeout(t);
  }, [catalogQ]);

  const organizationTypeOptions = useMemo(() => {
    const inData = new Set<string>();
    for (const p of participants) {
      const t = (p.organizationType || '').trim();
      if (t) inData.add(t);
    }
    const ordered = tiposOrganizacion.filter((t) => inData.has(t));
    const rest = Array.from(inData).filter((t) => !tiposOrganizacion.includes(t)).sort((a, b) =>
      a.localeCompare(b, 'es', { sensitivity: 'base' })
    );
    return [...ordered, ...rest];
  }, [participants]);

  const filtered = useMemo(() => {
    const q = debouncedQ.toLowerCase();
    const hasTypeFilter = filterOrganizationTypes.length > 0;
    return participants.filter((p) => {
      const orgType = (p.organizationType || '').trim();
      if (hasTypeFilter && !filterOrganizationTypes.includes(orgType)) {
        return false;
      }
      if (!q) return true;
      const hay = [
        p.fullName,
        p.organization,
        p.organizationType,
        p.position,
        p.email,
        p.phone,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [participants, debouncedQ, filterOrganizationTypes]);

  const hasActiveFilters = debouncedQ.trim() !== '' || filterOrganizationTypes.length > 0;

  const selectable = !!onSelectParticipant && canAddParticipant;

  const handleSelectParticipant = (p: BusinessRoundInterestedParticipant) => {
    const result = onSelectParticipant?.(p);
    if (!result?.ok) return;
    setAddNotice({ fullName: result.fullName, counterpartyNumber: result.counterpartyNumber });
    setRecentlyAddedId(p.entryId);
    window.setTimeout(() => setRecentlyAddedId(null), 2500);
  };

  useEffect(() => {
    if (!addNotice) return;
    const t = window.setTimeout(() => setAddNotice(null), ADD_FEEDBACK_MS);
    return () => window.clearTimeout(t);
  }, [addNotice]);

  if (!isOpen) return null;

  return (
    <div className="net-brr-page-backdrop" role="presentation" onClick={onClose}>
      <div
        className={`net-brr-page-panel${contentClassName ? ` ${contentClassName}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Participantes"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="net-brr-page-header">
          <div className="net-brr-page-header-row net-brr-page-header-row--close-only">
            <button
              type="button"
              className="net-brr-page-close"
              onClick={onClose}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          {onSelectParticipant && !canAddParticipant ? (
            <div className="net-brr-page-limit-banner" role="status" aria-live="polite">
              <span className="net-brr-page-limit-banner-icon" aria-hidden>
                !
              </span>
              <div className="net-brr-page-limit-banner-text">
                <strong>Límite de contrapartes alcanzado</strong>
                <span>
                  Ya cargaste el máximo de 5 contrapartes. Quitá una en el formulario para poder agregar otra desde
                  este listado.
                </span>
              </div>
            </div>
          ) : null}
          <div className="net-brr-page-filters">
            <div className="form-field net-initiatives-filter net-brr-page-filter">
              <label htmlFor="net-brr-filter-q">Buscar</label>
              <input
                id="net-brr-filter-q"
                type="search"
                className="form-input"
                placeholder="Nombre, organización, correo o teléfono…"
                value={catalogQ}
                onChange={(e) => setCatalogQ(e.target.value)}
                autoComplete="off"
              />
            </div>
            {organizationTypeOptions.length > 0 ? (
              <div className="form-field net-initiatives-filter net-brr-page-filter net-brr-page-filter--type">
                <span className="net-brr-filter-type-label" id="net-brr-filter-org-type-label">
                  Tipo de organización
                </span>
                <MultiSelectTags
                  options={organizationTypeOptions}
                  selectedValues={filterOrganizationTypes}
                  onChange={setFilterOrganizationTypes}
                  placeholder="Todos los tipos"
                  allowSearch={false}
                />
              </div>
            ) : null}
          </div>
          {addNotice ? (
            <div className="net-brr-add-feedback" role="status" aria-live="polite">
              <span className="net-brr-add-feedback-icon" aria-hidden>
                ✓
              </span>
              <span>
                Se agregó <strong>{addNotice.fullName}</strong> como Contraparte{' '}
                {addNotice.counterpartyNumber}. Podés seguir agregando o cerrar para ver el formulario.
              </span>
            </div>
          ) : null}
        </header>

        <main className="net-brr-page-main" aria-label="Listado de participantes">
          {loading && <p className="net-muted net-muted--tight">Cargando participantes…</p>}
          {error && (
            <p className="net-brr-error" role="alert">
              {error}
            </p>
          )}

          {!loading && !error && filtered.length === 0 && (
            <p className="net-muted net-muted--tight">
              {hasActiveFilters
                ? 'No hay resultados con los filtros aplicados.'
                : 'No hay participantes para mostrar.'}
            </p>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div
              className="net-initiative-list net-brr-page-list"
              role="list"
              aria-label="Participantes interesados en rondas"
            >
              {filtered.map((p) => {
                const alreadyAdded = isParticipantAlreadyInManualContacts(p, addedContacts);
                const showAdd = selectable && !alreadyAdded;
                return (
                  <ParticipantCatalogCard
                    key={p.entryId}
                    participant={p}
                    selectable={showAdd}
                    justAdded={recentlyAddedId === p.entryId}
                    onSelect={showAdd ? () => handleSelectParticipant(p) : undefined}
                  />
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BusinessRoundsInterestedModal;